import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, expand, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { isFreeSku } from '../../data/product/product-lookup.data';

/**
 * One purchasable SKU as `GET /store/catalog` serves it. Prices come from the
 * Stripe Price through the server; the site never carries an amount of its own.
 */
export interface StoreCatalogItem {
  sku: string;
  name: string;
  product: string;
  platform: string;
  version: string;
  includes: string[];
  unitAmount: number | null;
  currency: string;
  purchasable: boolean;
  /** a free listing (2026-09-21): no price; Add to cart puts it in a free order, or inside a paid one */
  free?: boolean;
}

/**
 * one copy of an order item: its own numbered download, named after the order
 * ("Codigrate Ocean (Sublime Text) CG-000012-3.zip"); the numbers run across
 * the whole order (two Oceans and a Prague are copies 1, 2 and 3)
 */
export interface StoreOrderCopy {
  number: number;
  fileName: string;
  downloadUrl: string | null;
  downloadExpiresAt: string | null;
}

export interface StoreOrderItem {
  id: number;
  sku: string;
  product: string;
  platform: string;
  name: string;
  version: string | null;
  fileName: string;
  /** copies bought (the download cap scales with it) */
  quantity: number;
  /** a free line (a free listing in a free or a mixed order) */
  free?: boolean;
  unitAmount?: number | null;
  downloadCount: number;
  /** null = unlimited */
  downloadsRemaining: number | null;
  /** the first copy's link (older servers: the only one) */
  downloadUrl: string | null;
  downloadExpiresAt: string | null;
  copies?: StoreOrderCopy[];
  /** the buyer's rating of this line, null until given */
  review?: StoreReview | null;
}

/** a buyer's rating of one order line; it shows on the site once the owner approves it */
export interface StoreReview {
  rating: number;
  comment: string | null;
  displayName: string | null;
  status: 'pending' | 'approved' | 'rejected';
  updatedAt: string;
}

/** count + average of the ratings of a sku or a theme */
export interface StoreRatingSummary {
  count: number;
  average: number;
}

/** an approved comment, as the theme pages show it */
export interface StorePublicReview {
  sku: string;
  product: string;
  platform: string;
  rating: number;
  comment: string;
  displayName: string;
  date: string;
}

/** GET /store/reviews */
export interface StoreReviews {
  ok: boolean;
  skus: Record<string, StoreRatingSummary>;
  products: Record<string, StoreRatingSummary>;
  reviews: StorePublicReview[];
}

/**
 * The catalogue as the site holds it: `ok` false when the store API could not
 * be reached (network, server down, the endpoint not deployed), which is not
 * the same as an empty catalogue: nothing is known to be gone, nothing can be
 * bought until it answers.
 */
export interface StoreCatalog {
  ok: boolean;
  items: StoreCatalogItem[];
}

/** one cart line as POST /store/checkout takes it */
export interface StoreCheckoutItem {
  sku: string;
  quantity: number;
}

export type StoreOrderStatus = 'pending' | 'paid' | 'failed' | 'expired' | 'refunded' | 'disputed';

export interface StoreOrder {
  ok: boolean;
  error?: string;
  status: StoreOrderStatus;
  orderNumber: string;
  token: string;
  createdAt: string;
  paidAt: string | null;
  email: string | null;
  customerName: string | null;
  currency: string | null;
  amountSubtotal: number | null;
  amountTax: number | null;
  amountTotal: number | null;
  receiptUrl: string | null;
  managedPayments: boolean;
  /** a free order: nothing was paid, no receipt, no invoice */
  free?: boolean;
  /** every copy in one zip; null with a single copy or before payment */
  downloadAllUrl?: string | null;
  downloadAllExpiresAt?: string | null;
  /** copies over the whole order (the last copy number) */
  copyCount?: number;
  /** the lines can be rated (a paid order) */
  reviewable?: boolean;
  /** the name a new review is signed with unless the buyer types another */
  reviewName?: string | null;
  items: StoreOrderItem[];
}

/**
 * what POST /store/checkout answers: Stripe's page (paid); for a free cart no url and no token, only
 * the order number and the address the downloads mail went to (the mail is the only way to the order page)
 */
export interface StoreCheckoutResult {
  ok: boolean;
  url: string | null;
  orderToken: string | null;
  orderNumber: string;
  /** the cart held free listings only: no payment, the downloads mail is on its way to `email` */
  free?: boolean;
  email?: string;
}

const CATALOG_REFRESH_MS = 10 * 60 * 1000;
/** a failed catalogue request is tried again this soon (the failure is shown meanwhile) */
const CATALOG_RETRY_MS = 30 * 1000;

/**
 * The store API (orchestration-server `/store`, see base-themes/STORE-DESIGN.md):
 * the catalogue with live prices, Checkout Session creation, the order page's
 * data and "find my order".
 */
@Injectable( {
  providedIn: 'root',
} )
export class StoreService {

  /**
   * The catalogue, shared by every subscriber and refreshed every 10 minutes
   * (a price change in the Stripe Dashboard is live everywhere within that).
   * A failed request yields { ok: false } at once, so pages show the listing's
   * own list price and the cart says the store is unreachable instead of
   * calling every item gone, and the request is tried again every 30 seconds
   * until it answers.
   */
  readonly catalog$: Observable<StoreCatalog> = timer( 0, CATALOG_REFRESH_MS ).pipe(
    switchMap( () => this.fetchCatalog().pipe(
      expand( catalog => catalog.ok ? EMPTY : timer( CATALOG_RETRY_MS ).pipe( switchMap( () => this.fetchCatalog() ) ) ),
    ) ),
    shareReplay( { bufferSize: 1, refCount: true } ),
  );

  /** the catalogue as a sku -> item map (empty while the store is unreachable). */
  readonly catalogBySku$: Observable<{ [ sku: string ]: StoreCatalogItem }> = this.catalog$.pipe(
    map( catalog => this.indexBySku( catalog ) ),
  );

  private fetchCatalog(): Observable<StoreCatalog> {
    return this.http.get<{ ok: boolean; items: StoreCatalogItem[] }>( `${ environment.serverUrl }/store/catalog` ).pipe(
      map( response => ( { ok: !!response && Array.isArray( response.items ), items: response?.items ?? [] } ) ),
      catchError( () => of( { ok: false, items: [] as StoreCatalogItem[] } ) ),
    );
  }

  /** sku -> item */
  indexBySku( catalog: StoreCatalog ): { [ sku: string ]: StoreCatalogItem } {
    const bySku: { [ sku: string ]: StoreCatalogItem } = {};
    for ( const item of catalog.items ) bySku[ item.sku ] = item;
    return bySku;
  }

  /** the latest catalogue, for synchronous price look-ups (priceLabel) */
  private bySku: { [ sku: string ]: StoreCatalogItem } = {};
  /** the catalogue request answered at least once (ok or not) */
  catalogSettled = false;
  /** the last catalogue answer came from the store API */
  catalogOk = false;

  constructor( private http: HttpClient ) {
    this.catalog$.subscribe( catalog => {
      this.catalogSettled = true;
      this.catalogOk = catalog.ok;
      this.bySku = this.indexBySku( catalog );
    } );
  }

  /** true when the store has confirmed the sku can be bought right now (a live Stripe price, or a free listing). */
  canBuy( sku: string | undefined ): boolean {
    const item = sku ? this.bySku[ sku ] : undefined;
    return this.catalogOk && !!item?.purchasable;
  }

  /** the catalogue's item for a sku, once it has answered */
  item( sku: string | undefined ): StoreCatalogItem | undefined {
    return sku ? this.bySku[ sku ] : undefined;
  }

  /**
   * What the Buy now CTA of a store listing may do: 'buy' when the store has
   * confirmed the sku, 'soon' when it answered without it (no Stripe price yet,
   * removed) or cannot be reached (the CTA reads "Coming soon", inert, like a
   * planned platform), 'loading' before the first answer (inert "Buy now").
   */
  availability( sku: string | undefined ): 'buy' | 'soon' | 'loading' {
    if ( !sku ) return 'soon';
    if ( !this.catalogSettled ) return 'loading';
    return this.canBuy( sku ) ? 'buy' : 'soon';
  }

  /**
   * Whether Add to cart may act: yes while the store is still answering
   * (kaizen 2026-09-20: the cart needs no price, and the first click after a
   * page load used to hit a disabled button), no once the store has said the
   * sku cannot be sold ('soon').
   */
  canAdd( sku: string | undefined ): boolean {
    return !!sku && this.availability( sku ) !== 'soon';
  }

  /**
   * The price to show for a sku right now: the catalogue's (live Stripe price)
   * when it has answered, else the listing's own list price from product.data,
   * else null (no flag).
   */
  /** The catalogue's version for a sku ('' until the catalogue answers). */
  version( sku: string | undefined ): string {
    return this.item( sku )?.version || '';
  }

  priceLabel( sku: string | undefined, fallback?: { amount: number; currency: string } ): string | null {
    const item = sku ? this.bySku[ sku ] : undefined;
    // a free listing (2026-09-21) reads "Free", never "$0.00"
    if ( item?.free || item?.unitAmount === 0 ) return 'Free';
    if ( item && item.unitAmount !== null && item.unitAmount !== undefined ) return this.formatPrice( item.unitAmount, item.currency );
    if ( fallback ) return this.formatPrice( fallback.amount, fallback.currency );
    if ( sku && isFreeSku( sku ) ) return 'Free';
    return null;
  }

  /** "$1.99" - the amount is in the currency's smallest unit; null -> null (no flag). */
  formatPrice( unitAmount: number | null | undefined, currency: string | null | undefined ): string | null {
    if ( unitAmount === null || unitAmount === undefined || !Number.isFinite( unitAmount ) ) return null;
    const code = ( currency || 'usd' ).toUpperCase();
    try {
      return new Intl.NumberFormat( 'en-US', { style: 'currency', currency: code, minimumFractionDigits: 2 } ).format( unitAmount / 100 );
    } catch {
      return `${ ( unitAmount / 100 ).toFixed( 2 ) } ${ code }`;
    }
  }

  /**
   * makes the order: the Stripe Checkout Session for a cart with paid lines
   * (resolves to Stripe's URL), or the free order for a cart of free listings
   * only (the e-mail is required, the URL is the order page, the downloads are ready).
   */
  /** `analyticsId`: this browser's Mixpanel id, so the order's Mixpanel events (sent by the server) join this visit */
  checkout( items: StoreCheckoutItem[], email?: string, marketingConsent?: boolean, analyticsId?: string | null ): Observable<StoreCheckoutResult> {
    return this.http.post<StoreCheckoutResult>(
      `${ environment.serverUrl }/store/checkout`,
      {
        items: items.map( item => ( { sku: item.sku, quantity: item.quantity } ) ),
        email: email || undefined,
        marketingConsent: marketingConsent === true ? true : undefined,
        locale: navigator?.language,
        analyticsId: analyticsId || undefined,
      },
    );
  }

  getOrder( token: string ): Observable<StoreOrder> {
    return this.http.get<StoreOrder>( `${ environment.serverUrl }/store/order/${ encodeURIComponent( token ) }` );
  }

  findOrder( email: string ): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>( `${ environment.serverUrl }/store/order/find`, { email } );
  }

  /** rates one line of a paid order (one review per line; sending again replaces it) */
  submitReview( token: string, itemId: number, rating: number, comment: string, displayName: string ): Observable<{ ok: boolean; review: StoreReview }> {
    return this.http.post<{ ok: boolean; review: StoreReview }>(
      `${ environment.serverUrl }/store/order/${ encodeURIComponent( token ) }/review`,
      { itemId, rating, comment: comment || undefined, displayName: displayName || undefined },
    );
  }

  /** ratings per sku and theme + the approved comments, fetched once per visit */
  private reviews$?: Observable<StoreReviews>;
  reviews(): Observable<StoreReviews> {
    if ( !this.reviews$ ) {
      this.reviews$ = this.http.get<StoreReviews>( `${ environment.serverUrl }/store/reviews` ).pipe(
        catchError( () => of( { ok: false, skus: {}, products: {}, reviews: [] } as StoreReviews ) ),
        shareReplay( 1 ),
      );
    }
    return this.reviews$;
  }
}
